import { apiClient } from "@/lib/api";
import type {
  HandshakeSessionListResponse,
  HandshakeSessionPublicDetail,
  HandshakeSessionDetail,
  HandshakeSession,
  ProcessMessageResult,
} from "@/types";
import type {
  AdminHandshakeListParams,
  ConfirmHandshakePayload,
  SendMessagePayload,
} from "./handshakeService.types";

export type { BookingSource } from "./handshakeService.types";

export const handshakeKeys = {
  all: ["handshake"] as const,
  adminLists: () => [...handshakeKeys.all, "admin", "list"] as const,
  adminList: (params: AdminHandshakeListParams) =>
    [...handshakeKeys.adminLists(), params] as const,
};

/** FastAPI expects repeated keys for array params, e.g. ?statuses=A&statuses=B */
function handshakeSessionsQueryString(
  params?: AdminHandshakeListParams,
): string {
  const queryParams = new URLSearchParams();
  if (!params) return "";
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      value.forEach((item) => queryParams.append(key, String(item)));
    } else {
      queryParams.append(key, String(value));
    }
  }
  return queryParams.toString();
}

export const handshakeService = {
  getSessionDetail: async (
    sessionId: string,
    options?: { signal?: AbortSignal },
  ): Promise<HandshakeSessionPublicDetail> => {
    const response = await apiClient.get<HandshakeSessionPublicDetail>(
      `/handshake/sessions/${sessionId}`,
      options?.signal ? { signal: options.signal } : undefined,
    );
    return response.data;
  },

  sendMessage: async (
    sessionId: string,
    data: SendMessagePayload,
  ): Promise<ProcessMessageResult> => {
    const response = await apiClient.post<ProcessMessageResult>(
      `/handshake/sessions/${sessionId}/messages`,
      data,
    );
    return response.data;
  },

  adminSendMessage: async (
    sessionId: string,
    data: SendMessagePayload,
  ): Promise<ProcessMessageResult> => {
    const response = await apiClient.post<ProcessMessageResult>(
      `/handshake/admin/sessions/${sessionId}/messages`,
      data,
    );
    return response.data;
  },

  confirmSession: async (
    sessionId: string,
    data: ConfirmHandshakePayload,
  ): Promise<HandshakeSession> => {
    const response = await apiClient.post<HandshakeSession>(
      `/handshake/sessions/${sessionId}/confirm`,
      data,
    );
    return response.data;
  },

  adminConfirmSession: async (
    sessionId: string,
    data: ConfirmHandshakePayload,
  ): Promise<HandshakeSession> => {
    const response = await apiClient.post<HandshakeSession>(
      `/handshake/admin/sessions/${sessionId}/confirm`,
      data,
    );
    return response.data;
  },

  cancelSession: async (
    sessionId: string,
    reason: string,
  ): Promise<HandshakeSession> => {
    const response = await apiClient.post<HandshakeSession>(
      `/handshake/sessions/${sessionId}/cancel`,
      { reason },
    );
    return response.data;
  },

  reopenSession: async (sessionId: string): Promise<HandshakeSession> => {
    const response = await apiClient.post<HandshakeSession>(
      `/handshake/sessions/${sessionId}/reopen`,
    );
    return response.data;
  },

  adminReopenSession: async (sessionId: string): Promise<HandshakeSession> => {
    const response = await apiClient.post<HandshakeSession>(
      `/handshake/admin/sessions/${sessionId}/reopen`,
    );
    return response.data;
  },

  adminListSessions: async (
    params?: AdminHandshakeListParams,
  ): Promise<HandshakeSessionListResponse> => {
    const queryString = handshakeSessionsQueryString(params);
    const url = `/handshake/admin/sessions${queryString ? `?${queryString}` : ""}`;
    const response = await apiClient.get<HandshakeSessionListResponse>(url);
    return response.data;
  },

  adminGetSessionDetail: async (
    sessionId: string,
  ): Promise<HandshakeSessionDetail> => {
    const response = await apiClient.get<HandshakeSessionDetail>(
      `/handshake/admin/sessions/${sessionId}`,
    );
    return response.data;
  },
};
